import {
  PublicationService,
  SubscriptionService,
  TiktokService,
  UserPlanCount,
} from '../../../services';
import { getBusinessByClientId, getBusinessByLogin } from '../../../services/business';
import { mapNetworkKeysToTypeIds, normalizeNetworkList } from './socialNetworkMapper';
import { parseScheduleFieldsToDate, formatDateToScheduleFields } from '../../../shared/campaign/utils/scheduleTimezone';

const SUCCESS_STATUS = new Set([200, 201, 204]);

const getGuestUserId = () => {
  const rawUserData = window.localStorage.getItem('userData');
  if (!rawUserData) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(rawUserData);
    return parsed?.guestUserId;
  } catch {
    return undefined;
  }
};

const extractErrorMessage = (error, fallbackMessage) => {
  const apiMessage = error?.response?.data?.error?.message
    || error?.response?.data?.message
    || error?.message;
  return apiMessage || fallbackMessage;
};

const uploadPublicationImage = async (imageSource) => {
  if (!imageSource || typeof imageSource !== 'string') {
    throw new Error('Imagem da arte ausente.');
  }

  let payload;

  if (imageSource.startsWith('data:image')) {
    const match = String(imageSource).match(/^data:([A-Za-z-+\/]+);base64,(.+)$/);
    if (!match) {
      throw new Error('Imagem em formato invalido para upload de publicacao.');
    }

    const [, fileContentType, fileBase64] = match;
    const fileFormat = fileContentType.split('/')[1];

    payload = {
      fileName: 'publication',
      fileFormat,
      fileContentType,
      fileBase64,
    };
  } else if (imageSource.startsWith('http://') || imageSource.startsWith('https://')) {
    payload = {
      fileName: 'publication',
      fileFormat: 'png',
      fileContentType: 'image/png',
      fileBase64: '',
      fileUrl: imageSource,
    };
  } else {
    throw new Error('Formato de imagem nao suportado para upload.');
  }

  const response = await PublicationService.postPublicationImage(payload);

  if (!SUCCESS_STATUS.has(response?.responseStatus)) {
    throw new Error('Falha no upload da imagem da publicacao.');
  }

  if (!response?.data?.fileUrl || !response?.data?.fileName) {
    throw new Error('Resposta de upload invalida para imagem da publicacao.');
  }

  return response.data;
};

const resolveBusinessId = async (clientData) => {
  if (clientData?.uuid) {
    const response = await getBusinessByClientId(clientData.uuid);
    return response?.data?.[0]?.uuid;
  }

  const response = await getBusinessByLogin();
  return response?.data?.[0]?.uuid;
};

export const parseDateFromArt = (art) => {
  const publishDate = String(art?.publishDate || '').trim();
  const publishTime = String(art?.publishTime || '').trim();

  if (publishDate && publishTime) {
    return parseScheduleFieldsToDate(publishDate, publishTime);
  }

  if (art?.publicationDateTime) {
    const parsedDate = new Date(art.publicationDateTime);
    if (!Number.isNaN(parsedDate.getTime())) {
      return parsedDate;
    }
  }

  return null;
};

function buildSchedulePatchFromDate(scheduleDate) {
  const { publishDate, publishTime } = formatDateToScheduleFields(scheduleDate);
  const publicationDateTime = scheduleDate.toISOString();

  return {
    publishDate,
    publishTime,
    publicationDateTime,
    scheduledAt: publicationDateTime,
  };
}

const validateSettingsForNetworks = (selectedNetworks, networkSettings) => {
  if (selectedNetworks.includes('instagram')) {
    const instagramItems = networkSettings?.instagram?.instagramItems || [];
    if (!instagramItems.length) {
      return 'Selecione o tipo de publicacao do Instagram.';
    }
  }

  if (selectedNetworks.includes('facebook')) {
    const facebookSettings = networkSettings?.facebook || {};
    if (!facebookSettings?.facebookPageToken?.value) {
      return 'Selecione a pagina do Facebook.';
    }

    const facebookItems = facebookSettings?.facebookItems || [];
    if (!facebookItems.length) {
      return 'Selecione o tipo de publicacao do Facebook.';
    }
  }

  if (selectedNetworks.includes('tiktok')) {
    const tiktokSettings = networkSettings?.tiktok || {};
    if (!tiktokSettings?.privacyLevel) {
      return 'Selecione a privacidade do TikTok.';
    }

    if (!tiktokSettings?.headline || !String(tiktokSettings.headline).trim()) {
      return 'Preencha o titulo do video do TikTok.';
    }

    if (tiktokSettings?.music && !tiktokSettings?.musicConfirmation) {
      return 'Confirme o uso de musica autorizada no TikTok.';
    }

    if (
      tiktokSettings?.contentDisclosure &&
      !tiktokSettings?.yourBrand &&
      !tiktokSettings?.brandedContent
    ) {
      return 'Selecione o tipo de conteudo patrocinado no TikTok.';
    }
  }

  return null;
};

const buildPublicationMode = (networkSettings = {}) => {
  const publicationMode = {};

  const instagramItems = networkSettings?.instagram?.instagramItems || [];
  const facebookItems = networkSettings?.facebook?.facebookItems || [];

  if (instagramItems.length) {
    publicationMode.instagramItems = instagramItems;
  }

  if (facebookItems.length) {
    publicationMode.facebookItems = facebookItems;
  }

  return Object.keys(publicationMode).length > 0
    ? JSON.stringify(publicationMode)
    : 'FEED';
};

const updateCampaignPlanCount = async () => {
  try {
    const userPlanCount = await UserPlanCount.getUserPlanCountByUser();
    const userSubscription = await SubscriptionService.getSubscriptionByUser();
    const currentMonth = new Date(new Date().setDate(1));
    currentMonth.setDate(currentMonth.getDate() + 30);
    currentMonth.setHours(0, 0, 0, 0);

    const expirationDate = userSubscription?.data?.length > 0
      ? userSubscription.data[0].expiration
      : currentMonth;

    if (!userPlanCount?.data) {
      // No existing record — create one with initial count
      await UserPlanCount.postUserPlanCount({
        ownScheduleCount: 0,
        postOnDesignCount: 1,
        imageBankCount: 0,
        aiImageCount: 0,
        aiLegendCount: 0,
        aiMessageCount: 0,
        renewalDate: expirationDate,
      });
      return;
    }

    // Just increment postOnDesignCount — counter resets are handled
    // exclusively by Stripe webhook renewal events (accumulative model)
    await UserPlanCount.putUserPlanCount(userPlanCount.data.uuid, {
      ownScheduleCount: userPlanCount.data.ownScheduleCount,
      postOnDesignCount: userPlanCount.data.postOnDesignCount + 1,
      imageBankCount: userPlanCount.data.imageBankCount,
      aiImageCount: userPlanCount.data.aiImageCount,
      aiLegendCount: userPlanCount.data.aiLegendCount,
      aiMessageCount: userPlanCount.data.aiMessageCount,
      renewalDate: userPlanCount.data.renewalDate,
    });
  } catch (error) {
    console.error('Erro ao atualizar contador de plano da campanha:', error);
  }
};

const buildNetworkSettings = (socialSelection = []) => {
  if (!Array.isArray(socialSelection)) {
    return {};
  }

  return socialSelection.reduce((acc, item) => {
    if (!item?.name) {
      return acc;
    }

    const normalizedName = String(item.name).trim().toLowerCase();
    acc[normalizedName] = item?.data || {};
    return acc;
  }, {});
};

const isPublishedArt = (art = {}) => {
  const normalizedScheduleStatus = String(art?.scheduleStatus || '').trim().toLowerCase();
  const normalizedPublicationStatus = String(art?.publicationStatus || '').trim().toLowerCase();

  return (
    normalizedScheduleStatus === 'published'
    || normalizedPublicationStatus === 'published'
    || normalizedPublicationStatus === 'publicado'
  );
};

export const scheduleCampaignPublications = async ({
  arts = [],
  socialSelection = [],
  clientData = null,
  campaignId = null,
  onStatusChange = () => {},
}) => {
  const businessId = await resolveBusinessId(clientData);
  if (!businessId) {
    throw new Error('BusinessId nao encontrado para agendamento.');
  }

  const guestId = getGuestUserId();
  const globalSelectedNetworks = normalizeNetworkList(
    socialSelection.map((item) => item?.name)
  );
  const globalNetworkSettings = buildNetworkSettings(socialSelection);

  const updatedArts = [];
  let successCount = 0;
  let failedCount = 0;

  for (let index = 0; index < arts.length; index++) {
    const art = arts[index];
    const pieceNumber = art?.piece_number ?? index + 1;

    let createdPublicationId = art?.publicationId || null;
    let resolvedSocialTypeIds = [];

    try {
      const selectedNetworks = normalizeNetworkList(
        Array.isArray(art?.socialMedias) && art.socialMedias.length
          ? art.socialMedias
          : globalSelectedNetworks
      );

      if (!selectedNetworks.length) {
        throw new Error('Selecione ao menos uma rede social para a arte.');
      }

      const artNetworkSettings = {
        ...globalNetworkSettings,
        ...(art?.socialMediaSettings || {}),
      };

      const settingsValidationError = validateSettingsForNetworks(
        selectedNetworks,
        artNetworkSettings
      );
      if (settingsValidationError) {
        throw new Error(settingsValidationError);
      }

      const scheduleDate = parseDateFromArt(art);
      if (!scheduleDate) {
        throw new Error('Data/hora invalida para agendamento da arte.');
      }

      resolvedSocialTypeIds = mapNetworkKeysToTypeIds(selectedNetworks);
      if (!resolvedSocialTypeIds.length) {
        throw new Error('Nao foi possivel mapear as redes sociais da arte.');
      }

      const shouldUpdateExistingPublication =
        !!art?.publicationId && !isPublishedArt(art);

      if (shouldUpdateExistingPublication) {
        onStatusChange(pieceNumber, { status: 'updating_publication' });

        const existingPublicationResponse = await PublicationService.getPublication(
          art.publicationId
        );

        if (!SUCCESS_STATUS.has(existingPublicationResponse?.responseStatus)) {
          throw new Error('Falha ao buscar publicacao para reagendamento.');
        }

        const rawData = existingPublicationResponse?.data;
        const existingPublication = Array.isArray(rawData)
          ? rawData[0] || {}
          : rawData || {};

        const publicationBody = {
          headline: art?.headline || existingPublication?.headline || '',
          legend: art?.legend || '',
          sketch: false,
          publicationImageUrlName:
            existingPublication?.publicationImageUrlName
            || art?.publicationImageUrlName
            || '',
          publicationImageUrl:
            existingPublication?.publicationImageUrl
            || art?.imageUrl
            || art?.url
            || '',
          status: existingPublication?.status || 'Agendado',
          isCanceled: false,
          scheduleDate,
          publicationDate: scheduleDate,
          jobId: existingPublication?.jobId,
          guestId,
          publicationMode:
            existingPublication?.publicationMode || buildPublicationMode(artNetworkSettings),
        };

        const updateResponse = await PublicationService.putPublication(
          art.publicationId,
          businessId,
          resolvedSocialTypeIds.join(','),
          publicationBody
        );

        if (!SUCCESS_STATUS.has(updateResponse?.responseStatus)) {
          throw new Error('Falha ao atualizar publicacao da arte.');
        }

        const schedulePatch = buildSchedulePatchFromDate(scheduleDate);
        const updatedArt = {
          ...art,
          socialMedias: selectedNetworks,
          socialMediaSettings: artNetworkSettings,
          publicationId: art.publicationId,
          ...schedulePatch,
          resolvedSocialTypes: resolvedSocialTypeIds,
          scheduleStatus: 'scheduled',
          errorMessage: null,
        };

        updatedArts.push(updatedArt);
        successCount += 1;

        onStatusChange(pieceNumber, {
          status: 'scheduled',
          publicationId: art.publicationId,
        });

        continue;
      }

      onStatusChange(pieceNumber, { status: 'uploading' });
      const publicationImage = await uploadPublicationImage(art?.imageUrl);

      const publicationBody = {
        headline: art?.headline || '',
        legend: art?.legend || '',
        publicationImageUrl: publicationImage.fileUrl,
        publicationImageUrlName: publicationImage.fileName,
        sketch: false,
        scheduleDate,
        publicationDate: scheduleDate,
        objective: '',
        businessClientId: clientData?.uuid || null,
        campaignId: campaignId || null,
        socialTypes: resolvedSocialTypeIds.join(','),
        guestId,
        generateArtJson: { params: [] },
        publicationMode: buildPublicationMode(artNetworkSettings),
        facebookPageToken: artNetworkSettings?.facebook?.facebookPageToken || null,
        originPublication: 'campaign',
      };

      onStatusChange(pieceNumber, { status: 'creating_publication' });
      const publicationResponse = await PublicationService.postPublication(
        businessId,
        resolvedSocialTypeIds.join(','),
        publicationBody
      );

      if (!SUCCESS_STATUS.has(publicationResponse?.responseStatus)) {
        throw new Error('Falha ao criar a publicacao da arte.');
      }

      createdPublicationId = publicationResponse?.data?.uuid;
      if (!createdPublicationId) {
        throw new Error('Publicacao criada sem identificador valido.');
      }

      if (resolvedSocialTypeIds.includes('3')) {
        const tiktokSettings = artNetworkSettings?.tiktok || {};
        const tiktokPayload = {
          publicationId: createdPublicationId,
          privacyLevel: tiktokSettings.privacyLevel,
          disableComment: !tiktokSettings.comment,
          musicImage: !!tiktokSettings.music,
          brandContentToggle: !!tiktokSettings.brandedContent,
          brandOrganicToggle: !!tiktokSettings.yourBrand,
        };

        onStatusChange(pieceNumber, { status: 'creating_tiktok_metadata' });
        const tiktokResponse = await TiktokService.postTiktokPublication(tiktokPayload);
        if (!SUCCESS_STATUS.has(tiktokResponse?.responseStatus)) {
          throw new Error('Falha ao criar metadados do TikTok para a arte.');
        }
      }

      await updateCampaignPlanCount();

      const schedulePatch = buildSchedulePatchFromDate(scheduleDate);
      const successArt = {
        ...art,
        socialMedias: selectedNetworks,
        socialMediaSettings: artNetworkSettings,
        publicationId: createdPublicationId,
        ...schedulePatch,
        resolvedSocialTypes: resolvedSocialTypeIds,
        scheduleStatus: 'scheduled',
        errorMessage: null,
      };

      updatedArts.push(successArt);
      successCount += 1;

      onStatusChange(pieceNumber, {
        status: 'scheduled',
        publicationId: createdPublicationId,
      });
    } catch (error) {
      const errorMessage = extractErrorMessage(
        error,
        'Falha ao agendar publicacao da arte.'
      );

      const failedArt = {
        ...art,
        publicationId: createdPublicationId || art?.publicationId || null,
        resolvedSocialTypes: resolvedSocialTypeIds,
        scheduleStatus: 'failed',
        errorMessage,
      };

      updatedArts.push(failedArt);
      failedCount += 1;

      onStatusChange(pieceNumber, {
        status: 'failed',
        errorMessage,
        publicationId: createdPublicationId || null,
      });
    }
  }

  return {
    updatedArts,
    summary: {
      total: arts.length,
      scheduled: successCount,
      failed: failedCount,
    },
  };
};
